import {useState, useEffect, useRef} from 'react';
import {Link, useNavigate} from 'react-router-dom';
import useMainStore from '@/stores/main';
import {SoopLoadingSpinner, SoopUiImg, SoopUiSwiper} from '@/components/ui';
import {PreviewAdPlayer} from '@/components/player/preview/PreviewAdPlayer';
import {PreviewPlayer} from '@/components/player/preview/PreviewPlayer';
import MyProfileImg from '@/components/my/common/item/MyProfileImg';
import {BANNER_RESPONSE_TYPE} from '@/constants/main/banner';
import {decodeHtmlEntities} from '@/utils';
import {convertViewCnt, isLogin} from '@/utils/main';
import VastInfo from '@/utils/player/VastInfo';
import globalDomains from '@/utils/global-domains';
import {sendClickLog, sendTopListLiveInflowLog} from '@/utils/main/my-plus/log';
import playerCss from '/public/static/css/components/player.module.scss';
import '/public/static/css/module/ad_player.scss';

const PLAYER_ERROR = {
  EXCEED_PLAY_LIMIT: 'EXCEED_PLAY_LIMIT', //1분 재생완료되었을떄 -> 라이브 이어보기 버튼 노출
  STATUS_ERROR: 'STATUS_ERROR', //플레이어 상태 변경 되었을때 -> 상태변경 안내 노출
};
const AUTO_PLAY_BLOCK = 'AUTO_PLAY_BLOCK';

export const PlayerArea = ({banner, isLoading, selectedMode}) => {
  const navigate = useNavigate();
  const [thumbsSwiper, setThumbsSwiper] = useState(null);
  const [imageError, setImageError] = useState(false);
  const [videoStatus, setVideoStatus] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [isVideoLoading, setIsVideoLoading] = useState(false);

  const [bannerPlaying, setBannerPlaying] = useState(false);
  const [bannerMuted, setBannerMuted] = useState(true);
  const [isBannerReady, setIsBannerReady] = useState(false);
  const [isLoadingRemindBanner, setIsLoadingRemindBanner] = useState(false);

  const navigationPrevRef = useRef(null);
  const navigationNextRef = useRef(null);

  const {topLiveList, checkAd} = useMainStore();

  const handleImageError = () => {
    setImageError(true);
  };

  const handleOnChangeSlide = (event) => {
    setActiveIndex(event.activeIndex);
    sendClickLog('main_top', {
      filterIdx: event.activeIndex + 1,
      button_type: 'live_banner',
    });
    setIsVideoLoading(false);
  };

  const handleOnClickLive = (item) => {
    if (videoStatus === PLAYER_ERROR.STATUS_ERROR) {
      return;
    }

    if (item.isBanner) {
      if (checkAd.AdParameter.hasOwnProperty('sendTracking')) {
        checkAd.AdParameter.sendTracking(item.subClickUrls);
        //리마인드 배너의 응답갑이 동영상형일떄만
        if (banner.type === BANNER_RESPONSE_TYPE.PC_MAIN_VIDEO) {
          const subVastInfo = VastInfo.getVastInfo(item.vast2);
          checkAd.AdParameter.sendTracking(
            subVastInfo.videoClicks.clickTracking,
          );
        }
      }
    } else {
      //클릭 로그
      sendClickLog('main_top', {
        buttonType: 'live_mainarea',
        contentsType: 'live',
        actionType: 'to_live',
      });
      //진입 로그
      sendTopListLiveInflowLog({
        streamerId: item.userId,
        broadcastId: item.broadcastId,
      });
    }
  };

  const swiperConfigMain = {
    slidesPerView: 4,
    thumbs: {
      swiper: thumbsSwiper && !thumbsSwiper.destroyed ? thumbsSwiper : null,
    },
    effect: 'fade',
    fadeEffect: {crossFade: true},
    allowTouchMove: true,
    loop: false,
    pagination: {el: '.player-pagination'},
    navigation: {
      prevEl: navigationPrevRef.current,
      nextEl: navigationNextRef.current,
    },
    onSlideChange: handleOnChangeSlide,
  };

  const swiperConfigSub = {
    direction: 'vertical',
    slidesPerView: 4,
    loop: false,
    freeMode: true,
    watchSlidesProgress: true,
    spaceBetween: 0,
  };

  useEffect(() => {
    if (topLiveList.list.length) {
      // 활성 동영상이 배너가 아닌 경우 로딩 화면 보이도록 조건 설정
      const {isBanner} = topLiveList?.list[activeIndex];
      setVideoStatus(null);
      setBannerPlaying(isBanner);
      if (isBanner) {
        // 리마인더 배너 그려진 적 있는지
        setIsLoadingRemindBanner(true);
      } else {
        setIsVideoLoading(true);
      }
    }
  }, [topLiveList, activeIndex]);

  const closeVideoLoading = (event) => {
    setIsVideoLoading(false);
  };

  const handlerAutoPlayBlock = (media) => {
    console.warn('autoPlayBlock ', media);
    setPlaying(false);
    setBannerPlaying(false);
    setVideoStatus(AUTO_PLAY_BLOCK);
    closeVideoLoading();
  };

  const handlePlayerError = (event) => {
    switch (event) {
      case PLAYER_ERROR.EXCEED_PLAY_LIMIT:
      case PLAYER_ERROR.STATUS_ERROR:
        setVideoStatus(event);
        break;
      default:
        break;
    }
    closeVideoLoading();
  };

  const handlerAdCanPlay = (event) => {
    setIsBannerReady(true);
  };

  //topLiveList가 0 이고 api콜 호출중이라면 스트리밍 스켈레톤 영역 노출
  if (topLiveList && topLiveList.count === 0 && isLoading) {
    return (
      <div className='player_wrap_skeleton'>
        <div></div>
      </div>
    );
  }

  return (
    topLiveList &&
    topLiveList.list?.length > 0 && (
      <div className={playerCss.player_wrap}>
        <div className={playerCss.player_box}>
          <SoopUiSwiper
            swiperClass={playerCss.player_view}
            swiperTag={'ul'}
            options={swiperConfigMain}
            maxSlidePerView={topLiveList.count}
            hasControls={false}
            handleSlideChanged={handleOnChangeSlide}>
            {topLiveList.list?.map(
              (item, idx) =>
                item && (
                  <li key={idx} className={item.isBanner ? playerCss.a_d : ''}>
                    <a
                      className={playerCss.wrap}
                      onClick={() => handleOnClickLive(item)}
                      href={
                        item.isBanner
                          ? item.landingUrl
                          : `${globalDomains.PLAY_AFREECATV}/${item.userId}/${item.broadcastId}`
                      }
                      target='_blank'>
                      <div className={playerCss.player}>
                        <div className={playerCss.inner}>
                          {(activeIndex === idx ||
                            (item.isBanner && isLoadingRemindBanner)) &&
                            (item.isBanner ? (
                              <>
                                {banner.type ===
                                  BANNER_RESPONSE_TYPE.PC_MAIN_IMAGE && (
                                  <div className='img' id='da_player'>
                                    <div className='poster'>
                                      <img
                                        src={item.thumbnailSrc}
                                        onError={(e) => {
                                          if (
                                            checkAd.AdParameter.hasOwnProperty(
                                              'sendTracking',
                                            )
                                          ) {
                                            checkAd.AdParameter.sendTracking(
                                              item.errorUrl,
                                            );
                                          }
                                        }}
                                        onLoad={(e) => {
                                          if (
                                            checkAd.AdParameter.hasOwnProperty(
                                              'sendTracking',
                                            )
                                          ) {
                                            checkAd.AdParameter.sendTracking(
                                              item.subImpressionUrls,
                                            );
                                            checkAd.AdParameter.sendTracking(
                                              item.subViewableUrls,
                                            );
                                          }
                                        }}
                                      />
                                    </div>
                                  </div>
                                )}
                                {banner.type ===
                                  BANNER_RESPONSE_TYPE.PC_MAIN_VIDEO && (
                                  // 리마인드 동영상형 배너는 기존 clickUrls, impressionUrls, viewableUrls, vast 가  subClickUrls, subImpressionUrls, subViewableUrls, vast2 값을 넣어줘야한다.
                                  <PreviewAdPlayer
                                    adInfo={{
                                      type: banner.type,
                                      exist: banner.exist,
                                      creatives: [
                                        {
                                          ...item,
                                          vast: item.vast2,
                                          clickUrls: item.subClickUrls,
                                          impressionUrls:
                                            item.subImpressionUrls,
                                          viewableUrls: item.subViewableUrls,
                                        },
                                      ],
                                    }}
                                    autoPlay={true}
                                    muted={bannerMuted}
                                    playing={bannerPlaying}
                                    visible={idx === activeIndex}
                                    onAutoPlayBlock={handlerAutoPlayBlock}
                                    onCanPlay={handlerAdCanPlay}
                                  />
                                )}
                              </>
                            ) : (
                              <PreviewPlayer
                                livePlayId={item.userId}
                                broadNo={item.broadcastId}
                                thumbnail={item.thumbnailSrc}
                                playDurationLimit={60}
                                refreshIntervalTime={10}
                                inflow={
                                  selectedMode === 'prefer' && isLogin()
                                    ? 'myplus'
                                    : 'popular'
                                }
                                autoPlay
                                playing={playing}
                                muted={true}
                                onAutoPlayBlock={handlerAutoPlayBlock}
                                onError={handlePlayerError}
                                onCanPlay={closeVideoLoading}
                              />
                            ))}
                        </div>
                      </div>
                      <div className={playerCss.live_info_area}>
                        <div className={playerCss.info_inner}>
                          <span
                            className={playerCss.thumb}
                            onClick={(e) => {
                              if (!item.isBanner) {
                                e.preventDefault();
                                e.stopPropagation(); // 클릭 이벤트 전파 방지
                                window.open(
                                  `${globalDomains.BJ_AFREECATV}/${item.userId}`,
                                );
                              }
                            }}>
                            {item.isBanner ? (
                              <img
                                src={item.profileImageUrl}
                                alt='프로필 이미지'
                                loading='lazy'
                              />
                            ) : (
                              <MyProfileImg userId={item.userId} />
                            )}
                          </span>
                          <div className={playerCss.info_wrap}>
                            <div className={playerCss.info}>
                              <p className={playerCss.nick}>
                                {item.userNickname}
                              </p>
                            </div>
                            <p className={playerCss.title}>
                              {decodeHtmlEntities(item.title)}
                            </p>
                            {!item.isBanner &&
                              item.categoryTags?.map((category) => (
                                <span
                                  key={category}
                                  className={playerCss.cate}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    navigate(
                                      `/directory/category/${encodeURIComponent(category)}/live`,
                                    );
                                  }}>
                                  {category}
                                </span>
                              ))}
                          </div>
                        </div>
                      </div>

                      {(videoStatus === PLAYER_ERROR.STATUS_ERROR ||
                        videoStatus === PLAYER_ERROR.EXCEED_PLAY_LIMIT ||
                        isVideoLoading) && (
                        <div
                          className={`${playerCss.state_wrap} ${isVideoLoading ? playerCss.loading : ''}`}>
                          {isVideoLoading && (
                            <SoopLoadingSpinner theme='dark' />
                          )}
                          {videoStatus === PLAYER_ERROR.STATUS_ERROR && (
                            <p className={playerCss.state}>
                              스트리머가 방송 상태를 변경하여 미리볼 수
                              없습니다.
                            </p>
                          )}
                          {videoStatus === PLAYER_ERROR.EXCEED_PLAY_LIMIT && (
                            <button
                              type='button'
                              className={playerCss.continue}>
                              라이브 이어보기
                            </button>
                          )}
                        </div>
                      )}
                    </a>

                    <div className={playerCss.btn_utils}>
                      {item.isBanner &&
                        banner.type === BANNER_RESPONSE_TYPE.PC_MAIN_VIDEO &&
                        isBannerReady && (
                          <button
                            type='button'
                            className={`${bannerMuted ? playerCss.soundOff : playerCss.soundOn}`}
                            onClick={() => {
                              setBannerMuted(!bannerMuted);
                            }}>
                            음소거/음소거 해제
                          </button>
                        )}
                      {videoStatus === AUTO_PLAY_BLOCK && (
                        <button
                          className={playerCss.playOn}
                          onClick={() => {
                            item.isBanner
                              ? setBannerPlaying(true)
                              : setPlaying(true);

                            setVideoStatus(null);
                          }}>
                          재생
                        </button>
                      )}
                    </div>
                  </li>
                ),
            )}
          </SoopUiSwiper>
        </div>
        {/* 스와이프 컨트롤바 */}
        <div className={`player-pagination`}></div>
        <button
          type='button'
          className={playerCss.btn_prev}
          ref={navigationPrevRef}>
          이전
        </button>
        <button
          type='button'
          className={playerCss.btn_next}
          ref={navigationNextRef}>
          다음
        </button>
        <SoopUiSwiper
          onSwiper={setThumbsSwiper}
          swiperClass={playerCss.thumb_list}
          hasControls={false}
          options={swiperConfigSub}
          maxSlidePerView={topLiveList.count}>
          {topLiveList.list.map((item, idx) => (
            <li key={idx}>
              <div className={playerCss.thumb_wrap}>
                <div className={playerCss.thumb}>
                  <SoopUiImg
                    type='banner'
                    src={item.thumbnailSrc}
                    onError={handleImageError}
                  />
                </div>
                <div className={playerCss.info_wrap}>
                  <p className={playerCss.nickName}>{item.userNickname}</p>
                  {item.isBanner ? (
                    <span className={playerCss.a_d_tag}>AD</span>
                  ) : (
                    <span>{convertViewCnt(item.viewCount)}명 참여 중</span>
                  )}
                </div>
              </div>
            </li>
          ))}
        </SoopUiSwiper>
      </div>
    )
  );
};
